/**
 * Knowledge Base management — admin authoring for help articles + categories.
 *
 * CSS prefix: kbm- (KnowledgeBase Management).
 *
 * Body editor: Tiptap with StarterKit + Link + Image + Placeholder. Output
 * is HTML, sanitized server-side via sanitize-html on save AND on preview.
 * We never trust the editor's HTML directly — the server is the source of
 * truth for what gets rendered on the public page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Book,
  Plus,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  X,
  Eye,
  CheckCircle2,
  XCircle,
  FileText,
  Folder,
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  ChevronDown,
} from 'lucide-react';
import './KnowledgeBaseManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const STATUSES = [
  { value: '', label: 'All status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const toSlug = (str) =>
  String(str || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// ============================================================================
// Tiptap editor toolbar
// ============================================================================

const ToolbarButton = ({ onClick, active, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`kbm-tb-btn ${active ? 'is-active' : ''}`}
    aria-pressed={active || false}
    title={label}
  >
    {children}
  </button>
);

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt('Image URL');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="kbm-toolbar">
      <div className="kbm-tb-group">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold">
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic">
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} label="Inline code">
          <Code size={14} />
        </ToolbarButton>
      </div>
      <span className="kbm-tb-sep" />
      <div className="kbm-tb-group">
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} label="Heading 1">
          <Heading1 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} label="Heading 2">
          <Heading2 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} label="Heading 3">
          <Heading3 size={14} />
        </ToolbarButton>
      </div>
      <span className="kbm-tb-sep" />
      <div className="kbm-tb-group">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet list">
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Numbered list">
          <ListOrdered size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} label="Quote">
          <Quote size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} label="Code block">
          <Code size={14} />
        </ToolbarButton>
      </div>
      <span className="kbm-tb-sep" />
      <div className="kbm-tb-group">
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} label="Insert link">
          <LinkIcon size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={addImage} label="Insert image">
          <ImageIcon size={14} />
        </ToolbarButton>
      </div>
      <span className="kbm-tb-sep" />
      <div className="kbm-tb-group">
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} label="Undo">
          <Undo size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} label="Redo">
          <Redo size={14} />
        </ToolbarButton>
      </div>
    </div>
  );
};

// ============================================================================
// Article editor modal
// ============================================================================

const emptyArticle = {
  id: null,
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  status: 'DRAFT',
  categoryId: '',
  tags: [],
  isFeatured: false,
  sortOrder: 0,
};

const ArticleEditor = ({ article, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(article || emptyArticle);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Image,
      Placeholder.configure({ placeholder: 'Write your article…' }),
    ],
    content: form.body || '',
    onUpdate: ({ editor: e }) => setForm((f) => ({ ...f, body: e.getHTML() })),
  });

  // Sync incoming article into editor when switching between articles.
  useEffect(() => {
    if (article) setForm(article);
    if (editor && article?.body !== undefined) {
      editor.commands.setContent(article.body || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id]);

  const handleField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value }));

  const handleSlugFromTitle = () => {
    if (!form.slug && form.title) setForm((f) => ({ ...f, slug: toSlug(f.title) }));
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const save = async (statusOverride) => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || toSlug(form.title),
        excerpt: form.excerpt.trim() || null,
        body: form.body,
        status: statusOverride || form.status,
        categoryId: form.categoryId || null,
        tags: form.tags,
        isFeatured: !!form.isFeatured,
        sortOrder: Number(form.sortOrder) || 0,
      };
      const url = form.id
        ? `${API_BASE}/api/admin/knowledgebase/articles/${form.id}`
        : `${API_BASE}/api/admin/knowledgebase/articles`;
      const res = await fetch(url, {
        method: form.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Save failed (${res.status})`);
      }
      onSaved(data.data);
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewLoading(true);
    setPreviewHtml(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: form.body }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data?.error || 'Preview failed');
      setPreviewHtml(data.data.body);
    } catch (err) {
      alert(err.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="kbm-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="kbm-editor-title">
      <div className="kbm-modal">
        <header className="kbm-modal-head">
          <h2 id="kbm-editor-title">{form.id ? 'Edit article' : 'New article'}</h2>
          <button type="button" className="kbm-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="kbm-modal-body">
          <div className="kbm-editor-grid">
            <div className="kbm-editor-main">
              <label className="kbm-field">
                <span className="kbm-field-label">Title</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={handleField('title')}
                  onBlur={handleSlugFromTitle}
                  className="kbm-input"
                  placeholder="How to set up Paystack"
                />
              </label>

              <label className="kbm-field">
                <span className="kbm-field-label">Slug</span>
                <input
                  type="text"
                  value={form.slug}
                  onChange={handleField('slug')}
                  className="kbm-input kbm-mono"
                  placeholder="how-to-set-up-paystack"
                />
                <small className="kbm-field-help">Lives at <code>/knowledgebase/&lt;slug&gt;</code>. Auto-generated from the title if left empty.</small>
              </label>

              <label className="kbm-field">
                <span className="kbm-field-label">Excerpt</span>
                <textarea
                  value={form.excerpt}
                  onChange={handleField('excerpt')}
                  className="kbm-input kbm-textarea"
                  rows={2}
                  placeholder="One-sentence summary shown in search and category listings."
                />
              </label>

              <div className="kbm-field">
                <span className="kbm-field-label">Body</span>
                <div className="kbm-editor-shell">
                  <EditorToolbar editor={editor} />
                  <EditorContent editor={editor} className="kbm-editor-content" />
                </div>
                <small className="kbm-field-help">HTML is sanitized server-side before storage. 200KB max.</small>
              </div>
            </div>

            <aside className="kbm-editor-side">
              <div className="kbm-side-card">
                <h3 className="kbm-side-title">Publish</h3>
                <label className="kbm-field">
                  <span className="kbm-field-label">Status</span>
                  <div className="kbm-field-select">
                    <select value={form.status} onChange={handleField('status')}>
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                    <ChevronDown size={14} aria-hidden="true" />
                  </div>
                </label>
                <div className="kbm-side-actions">
                  <button type="button" className="kbm-btn kbm-btn-secondary" onClick={showPreview} disabled={previewLoading || !form.body}>
                    {previewLoading ? <RefreshCw size={13} className="kbm-spin" /> : <Eye size={13} />}
                    <span>Preview</span>
                  </button>
                  <button type="button" className="kbm-btn kbm-btn-secondary" onClick={() => save('DRAFT')} disabled={saving}>
                    {saving ? <RefreshCw size={13} className="kbm-spin" /> : <FileText size={13} />}
                    <span>Save draft</span>
                  </button>
                  <button type="button" className="kbm-btn kbm-btn-primary" onClick={() => save('PUBLISHED')} disabled={saving}>
                    {saving ? <RefreshCw size={13} className="kbm-spin" /> : <CheckCircle2 size={13} />}
                    <span>{form.status === 'PUBLISHED' ? 'Update published' : 'Publish'}</span>
                  </button>
                </div>
              </div>

              <div className="kbm-side-card">
                <h3 className="kbm-side-title">Organize</h3>
                <label className="kbm-field">
                  <span className="kbm-field-label">Category</span>
                  <div className="kbm-field-select">
                    <select value={form.categoryId || ''} onChange={handleField('categoryId')}>
                      <option value="">Uncategorized</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} aria-hidden="true" />
                  </div>
                </label>

                <div className="kbm-field">
                  <span className="kbm-field-label">Tags</span>
                  <div className="kbm-tag-input">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Press Enter"
                      className="kbm-input"
                    />
                  </div>
                  <div className="kbm-tags">
                    {form.tags.map((t) => (
                      <span key={t} className="kbm-tag">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <label className="kbm-checkbox">
                  <input type="checkbox" checked={!!form.isFeatured} onChange={handleField('isFeatured')} />
                  <span>Featured on KB homepage</span>
                </label>

                <label className="kbm-field">
                  <span className="kbm-field-label">Sort order</span>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={handleField('sortOrder')}
                    className="kbm-input"
                  />
                  <small className="kbm-field-help">Lower numbers appear first within a category.</small>
                </label>
              </div>
            </aside>
          </div>

          {previewHtml !== null && (
            <div className="kbm-preview" aria-live="polite">
              <div className="kbm-preview-head">
                <h3>Preview (sanitized)</h3>
                <button type="button" className="kbm-icon-btn" onClick={() => setPreviewHtml(null)} aria-label="Close preview">
                  <X size={14} />
                </button>
              </div>
              <article
                className="kbm-preview-body"
                /* eslint-disable-next-line react/no-danger -- output is server-sanitized */
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Category editor modal
// ============================================================================

const emptyCategory = {
  id: null,
  name: '',
  slug: '',
  description: '',
  icon: '',
  sortOrder: 0,
  isActive: true,
};

const CategoryEditor = ({ category, onClose, onSaved }) => {
  const [form, setForm] = useState(category || emptyCategory);
  const [saving, setSaving] = useState(false);
  const handle = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name.trim()) return alert('Name is required');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || toSlug(form.name),
        description: form.description.trim() || null,
        icon: form.icon.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: !!form.isActive,
      };
      const url = form.id
        ? `${API_BASE}/api/admin/knowledgebase/categories/${form.id}`
        : `${API_BASE}/api/admin/knowledgebase/categories`;
      const res = await fetch(url, {
        method: form.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data?.error || 'Save failed');
      onSaved(data.data);
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kbm-modal-overlay" role="dialog" aria-modal="true">
      <div className="kbm-modal kbm-modal-sm">
        <header className="kbm-modal-head">
          <h2>{form.id ? 'Edit category' : 'New category'}</h2>
          <button type="button" className="kbm-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="kbm-modal-body">
          <label className="kbm-field">
            <span className="kbm-field-label">Name</span>
            <input type="text" value={form.name} onChange={handle('name')} className="kbm-input" placeholder="Getting started" />
          </label>
          <label className="kbm-field">
            <span className="kbm-field-label">Slug</span>
            <input
              type="text"
              value={form.slug}
              onChange={handle('slug')}
              className="kbm-input kbm-mono"
              placeholder="getting-started"
            />
            <small className="kbm-field-help">Auto-generated from the name if left blank.</small>
          </label>
          <label className="kbm-field">
            <span className="kbm-field-label">Description</span>
            <textarea value={form.description} onChange={handle('description')} className="kbm-input kbm-textarea" rows={2} />
          </label>
          <label className="kbm-field">
            <span className="kbm-field-label">Icon (lucide name)</span>
            <input
              type="text"
              value={form.icon}
              onChange={handle('icon')}
              className="kbm-input kbm-mono"
              placeholder="ShoppingCart"
            />
          </label>
          <label className="kbm-field">
            <span className="kbm-field-label">Sort order</span>
            <input type="number" value={form.sortOrder} onChange={handle('sortOrder')} className="kbm-input" />
          </label>
          <label className="kbm-checkbox">
            <input type="checkbox" checked={!!form.isActive} onChange={handle('isActive')} />
            <span>Active (visible on the public KB)</span>
          </label>
          <div className="kbm-modal-footer">
            <button type="button" className="kbm-btn kbm-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="kbm-btn kbm-btn-primary" onClick={save} disabled={saving}>
              {saving ? <RefreshCw size={13} className="kbm-spin" /> : <CheckCircle2 size={13} />}
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main page
// ============================================================================

const STATUS_CHIP = {
  PUBLISHED: { label: 'Published', cls: 'kbm-chip-on' },
  DRAFT: { label: 'Draft', cls: 'kbm-chip-draft' },
  ARCHIVED: { label: 'Archived', cls: 'kbm-chip-archived' },
};

const KnowledgeBaseManagement = () => {
  const [tab, setTab] = useState('articles');

  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const [editing, setEditing] = useState(null); // article being edited, or null
  const [editingCategory, setEditingCategory] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/categories`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setCategories(data.data || []);
    } catch {
      /* surfaced via list page on next fetch */
    } finally {
      setLoadingCats(false);
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('categoryId', categoryFilter);
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/articles?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setArticles(data.data || []);
        setPagination(data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
      }
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, categoryFilter]);

  const openEdit = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/articles/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) throw new Error(data?.error || 'Failed to load');
      setEditing(data.data);
    } catch (err) {
      alert(err.message || 'Failed to load article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/articles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data?.error || 'Delete failed');
      fetchArticles();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleDeleteCategory = async (id, articleCount) => {
    if (articleCount > 0) {
      if (!window.confirm(`This category has ${articleCount} articles. They will become uncategorized. Continue?`)) return;
    } else if (!window.confirm('Delete this category?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledgebase/categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data?.error || 'Delete failed');
      fetchCategories();
      fetchArticles();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const categoryMap = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  return (
    <div className="kbm-page">
      <header className="kbm-header">
        <div className="kbm-header-text">
          <h1 className="kbm-title">
            <Book size={22} />
            <span>Knowledge Base</span>
          </h1>
          <p className="kbm-subtitle">
            Author public help articles. Body content is sanitized server-side before publish.
          </p>
        </div>
        <div className="kbm-header-actions">
          <button
            type="button"
            className="kbm-btn kbm-btn-secondary"
            onClick={tab === 'articles' ? fetchArticles : fetchCategories}
            disabled={loading || loadingCats}
          >
            <RefreshCw size={14} className={loading || loadingCats ? 'kbm-spin' : ''} />
            <span>Refresh</span>
          </button>
          {tab === 'articles' ? (
            <button type="button" className="kbm-btn kbm-btn-primary" onClick={() => setEditing(emptyArticle)}>
              <Plus size={14} />
              <span>New article</span>
            </button>
          ) : (
            <button type="button" className="kbm-btn kbm-btn-primary" onClick={() => setEditingCategory(emptyCategory)}>
              <Plus size={14} />
              <span>New category</span>
            </button>
          )}
        </div>
      </header>

      <div className="kbm-tabs" role="tablist" aria-label="Knowledge Base sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'articles'}
          className={`kbm-tab ${tab === 'articles' ? 'is-active' : ''}`}
          onClick={() => setTab('articles')}
        >
          <FileText size={14} />
          <span>Articles</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'categories'}
          className={`kbm-tab ${tab === 'categories' ? 'is-active' : ''}`}
          onClick={() => setTab('categories')}
        >
          <Folder size={14} />
          <span>Categories</span>
        </button>
      </div>

      {tab === 'articles' ? (
        <>
          <section className="kbm-filter-bar">
            <div className="kbm-search">
              <Search size={14} className="kbm-search-icon" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles by title, slug, or excerpt"
                className="kbm-search-input"
              />
            </div>
            <div className="kbm-filters">
              <label className="kbm-field-inline">
                <span className="kbm-field-label">Status</span>
                <div className="kbm-field-select">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} aria-hidden="true" />
                </div>
              </label>
              <label className="kbm-field-inline">
                <span className="kbm-field-label">Category</span>
                <div className="kbm-field-select">
                  <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <option value="">All categories</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} aria-hidden="true" />
                </div>
              </label>
            </div>
          </section>

          <section className="kbm-table-wrap">
            <table className="kbm-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th className="kbm-num">Views</th>
                  <th>Updated</th>
                  <th className="kbm-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && articles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="kbm-table-empty">
                      <RefreshCw size={14} className="kbm-spin" /> Loading…
                    </td>
                  </tr>
                ) : articles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="kbm-table-empty">
                      No articles yet. Click <strong>New article</strong> to write your first.
                    </td>
                  </tr>
                ) : (
                  articles.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="kbm-cell-name">
                          <strong>{a.title}</strong>
                          <span className="kbm-cell-sub">
                            /{a.slug}
                            {a.isFeatured ? <span className="kbm-cell-pill"> Featured</span> : null}
                          </span>
                        </div>
                      </td>
                      <td>{a.category?.name || <span className="kbm-cell-sub">—</span>}</td>
                      <td>
                        <span className={`kbm-chip ${STATUS_CHIP[a.status]?.cls || ''}`}>
                          {STATUS_CHIP[a.status]?.label || a.status}
                        </span>
                      </td>
                      <td className="kbm-num">{(a.viewCount || 0).toLocaleString()}</td>
                      <td>{formatDate(a.updatedAt)}</td>
                      <td className="kbm-actions-col">
                        <div className="kbm-actions">
                          <button
                            type="button"
                            className="kbm-icon-btn"
                            onClick={() => openEdit(a.id)}
                            title="Edit"
                            aria-label="Edit article"
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            type="button"
                            className="kbm-icon-btn kbm-icon-btn-danger"
                            onClick={() => handleDelete(a.id)}
                            title="Delete"
                            aria-label="Delete article"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          {pagination.pages > 1 && (
            <footer className="kbm-pager">
              <button
                type="button"
                className="kbm-btn kbm-btn-secondary"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="kbm-pager-text">
                Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} articles
              </span>
              <button
                type="button"
                className="kbm-btn kbm-btn-secondary"
                disabled={page >= pagination.pages || loading}
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              >
                Next
              </button>
            </footer>
          )}
        </>
      ) : (
        <section className="kbm-table-wrap">
          <table className="kbm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th className="kbm-num">Articles</th>
                <th>Status</th>
                <th>Sort</th>
                <th className="kbm-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingCats && categories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="kbm-table-empty">
                    <RefreshCw size={14} className="kbm-spin" /> Loading…
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="kbm-table-empty">
                    No categories yet. Articles can still live without one, but categories make the KB easier to browse.
                  </td>
                </tr>
              ) : (
                categories.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="kbm-cell-name">
                        <strong>{c.name}</strong>
                        {c.description ? <span className="kbm-cell-sub">{c.description}</span> : null}
                      </div>
                    </td>
                    <td className="kbm-mono">/{c.slug}</td>
                    <td className="kbm-num">{c.articleCount || 0}</td>
                    <td>
                      <span className={`kbm-chip ${c.isActive ? 'kbm-chip-on' : 'kbm-chip-archived'}`}>
                        {c.isActive ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td>{c.sortOrder}</td>
                    <td className="kbm-actions-col">
                      <div className="kbm-actions">
                        <button
                          type="button"
                          className="kbm-icon-btn"
                          onClick={() => setEditingCategory(c)}
                          title="Edit"
                          aria-label="Edit category"
                        >
                          <Edit size={13} />
                        </button>
                        <button
                          type="button"
                          className="kbm-icon-btn kbm-icon-btn-danger"
                          onClick={() => handleDeleteCategory(c.id, c.articleCount || 0)}
                          title="Delete"
                          aria-label="Delete category"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}

      {editing && (
        <ArticleEditor
          article={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchArticles();
          }}
        />
      )}
      {editingCategory && (
        <CategoryEditor
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSaved={() => {
            setEditingCategory(null);
            fetchCategories();
            fetchArticles();
          }}
        />
      )}
    </div>
  );
};

export default KnowledgeBaseManagement;
